/**
 * Central Admin RBAC catalogue — the ONE authoritative permission matrix.
 *
 * - The admin-api synchronises these roles/permissions into the Central Admin
 *   database on startup and enforces them on every route (authoritative).
 * - The web app reads the signed-in user's permissions only to decide what to show.
 * - Tests check every route against this matrix, and
 *   docs/ADMIN_ROLES_AND_PERMISSIONS.md is generated from it
 *   (`npm run docs:permissions`); a test fails if the document drifts.
 *
 * Holding a permission never implies that a production operation exists:
 * e.g. `license.manage` only unlocks license UI; no license is issued until a
 * separately approved production-linking phase (RBAC ≠ technical availability).
 */
export const PERMISSIONS = {
    'business.read': 'View business categories and business master records',
    'business.create': 'Create business master records',
    'business.update': 'Edit business master records',
    'pharmacy.read': 'View pharmacy profiles',
    'pharmacy.create': 'Create pharmacies',
    'pharmacy.update': 'Edit pharmacy business and contact information',
    'branch.read': 'View branches',
    'branch.create': 'Create branches',
    'branch.update': 'Edit branches',
    'license.manage': 'Issue and renew production licenses through the Integration API',
    'license.read': 'View license information (planned and production when linked)',
    'update.read': 'View application update information',
    'update.manage': 'Manage application updates (releases, deployments, update customers); reveal recoverable Update Codes',
    'device.read': 'View POS devices',
    'device.manage': 'Manage POS devices (not connected to production yet)',
    'api.read': 'View API and integration information',
    'api.manage': 'Manage API keys and integrations; reveal recoverable Branch API Keys',
    'mobile.read': 'View owner mobile access',
    'mobile.manage': 'Manage owner mobile access (not connected to production yet)',
    'activity.read': 'View customer activity (pharmacy and branch events)',
    'audit.read': 'View the full audit trail, including sign-ins and admin user changes',
    'admin.read': 'View admin users',
    'admin.create': 'Create admin users',
    'admin.update': 'Edit admin users, assign roles, activate/deactivate and reset passwords',
    'settings.read': 'View system settings and status',
    'settings.manage': 'Change system settings (none are editable yet)',
    'production.read': 'View production identities, connection status and the Production Linking workspace (read-only)',
    'production.link': 'Confirm, reject, retire and classify production identity links; create businesses from candidates',
    'tenant.manage': 'Create and update Production tenants (customer provisioning) through the Integration API',
    'enrollment.manage': 'Reveal and manage POS branch enrollment codes through the Integration API',
    'inventory_app.read': 'View the global Inventory App status and settings',
    'inventory_app.manage': 'Manage the global Inventory App (activate/deactivate, password, force logout)',
    'sync.read': 'View Production branch sync status, online estimates, and read-only cloud inventory',
    'pos_operation.read': 'View POS operational status (ACTIVE/SUSPENDED) for linked branches',
    'pos_operation.manage': 'Suspend or reactivate POS operational status for linked branches',
    'pos_credentials.read': 'View reported POS local users and credential command metadata',
    'pos_credentials.manage': 'Create remote POS username/password credential commands for linked branches',
};
export const PERMISSION_KEYS = Object.keys(PERMISSIONS);
export const ROLE_KEYS = ['SUPER_ADMIN', 'TECHNICAL_ADMIN', 'SUPPORT', 'SALES'];
const CUSTOMER_READ = ['business.read', 'pharmacy.read', 'branch.read'];
const CUSTOMER_WRITE = ['business.create', 'business.update', 'pharmacy.create', 'pharmacy.update', 'branch.create', 'branch.update'];
const TECHNICAL_READ = [
    'license.read',
    'update.read',
    'device.read',
    'api.read',
    'mobile.read',
    'production.read',
    'inventory_app.read',
    'sync.read',
    'pos_operation.read',
    'pos_credentials.read',
];
const TECHNICAL_MANAGE = [
    'license.manage',
    'update.manage',
    'device.manage',
    'api.manage',
    'mobile.manage',
    'production.link',
    'tenant.manage',
    'enrollment.manage',
    'inventory_app.manage',
    'pos_operation.manage',
    'pos_credentials.manage',
];
export const ROLE_DEFINITIONS = [
    {
        key: 'SUPER_ADMIN',
        name: 'Super Admin',
        description: 'Full control of Central Admin, including staff accounts, roles and settings.',
        permissions: PERMISSION_KEYS,
    },
    {
        key: 'TECHNICAL_ADMIN',
        name: 'Technical Admin',
        description: 'Technical management of customer systems. No authority over staff accounts.',
        permissions: [...CUSTOMER_READ, ...CUSTOMER_WRITE, ...TECHNICAL_READ, ...TECHNICAL_MANAGE, 'activity.read', 'settings.read'],
    },
    {
        key: 'SUPPORT',
        name: 'Support',
        description: 'Read-oriented customer support and troubleshooting. Cannot change customer data.',
        permissions: [
            ...CUSTOMER_READ,
            'license.read',
            'update.read',
            'device.read',
            'api.read',
            'mobile.read',
            'production.read',
            'inventory_app.read',
            'sync.read',
            'activity.read',
            'settings.read',
        ],
    },
    {
        key: 'SALES',
        name: 'Sales',
        description: 'Customer and business information, including onboarding. No technical or security administration.',
        // license.read: Sales records the planned (purchased) license during onboarding.
        // No production.read / production.link / sync.read — technical linking is out of scope for Sales.
        permissions: [...CUSTOMER_READ, ...CUSTOMER_WRITE, 'license.read', 'settings.read'],
    },
];
export function getRoleDefinition(key) {
    const role = ROLE_DEFINITIONS.find((r) => r.key === key);
    if (!role)
        throw new Error(`Unknown role: ${key}`);
    return role;
}
export function isRoleKey(value) {
    return typeof value === 'string' && ROLE_KEYS.includes(value);
}
export function roleHasPermission(role, permission) {
    return getRoleDefinition(role).permissions.includes(permission);
}
export const CAPABILITIES = [
    { label: 'Business categories & businesses', view: 'business.read', manage: ['business.create', 'business.update'] },
    { label: 'Pharmacies (business & contact information)', view: 'pharmacy.read', manage: ['pharmacy.create', 'pharmacy.update'] },
    { label: 'Branches', view: 'branch.read', manage: ['branch.create', 'branch.update'] },
    { label: 'Licenses', view: 'license.read', manage: ['license.manage'], futureOperation: true },
    { label: 'Application updates', view: 'update.read', manage: ['update.manage'], futureOperation: true },
    { label: 'POS devices', view: 'device.read', manage: ['device.manage'], futureOperation: true },
    { label: 'POS operational status', view: 'pos_operation.read', manage: ['pos_operation.manage'] },
    { label: 'POS remote credentials', view: 'pos_credentials.read', manage: ['pos_credentials.manage'] },
    { label: 'API & integrations', view: 'api.read', manage: ['api.manage'], futureOperation: true },
    { label: 'Owner mobile access', view: 'mobile.read', manage: ['mobile.manage'], futureOperation: true },
    { label: 'Production identities & linking', view: 'production.read', manage: ['production.link'] },
    {
        label: 'Production provisioning (tenants, branches, enrollment)',
        view: 'production.read',
        manage: ['tenant.manage', 'enrollment.manage'],
        note: 'Creates real Production tenants/branches; each write links a confirmed ExternalIdentityLink.',
    },
    { label: 'Inventory App (global)', view: 'inventory_app.read', manage: ['inventory_app.manage'], note: 'One global Inventory App — not per pharmacy.' },
    {
        label: 'Sync status & cloud inventory (read)',
        view: 'sync.read',
        note: 'ONLINE ≤10m, STALE ≤24h, OFFLINE otherwise / never synced. Cloud inventory is read-only; CRUD retired.',
    },
    { label: 'Customer activity', view: 'activity.read' },
    { label: 'Full audit trail (sign-ins, staff changes)', view: 'audit.read' },
    { label: 'Admin users, roles, activation & password reset', view: 'admin.read', manage: ['admin.create', 'admin.update'] },
    { label: 'Settings', view: 'settings.read', manage: ['settings.manage'], note: 'No setting is editable yet.' },
];
/** Access level for a set of held permissions (e.g. a signed-in user's). */
export function accessLevelFor(held, capability) {
    if (!held.includes(capability.view))
        return 'No Access';
    if (capability.manage?.length && capability.manage.every((p) => held.includes(p)))
        return 'Manage';
    return 'View';
}
export function accessLevel(role, capability) {
    return accessLevelFor(getRoleDefinition(role).permissions, capability);
}
/** Markdown table embedded in docs/ADMIN_ROLES_AND_PERMISSIONS.md. */
export function renderCapabilityMatrixMarkdown() {
    const header = `| Capability | ${ROLE_DEFINITIONS.map((r) => r.name).join(' | ')} |`;
    const divider = `| --- | ${ROLE_DEFINITIONS.map(() => ':-:').join(' | ')} |`;
    const rows = CAPABILITIES.map((c) => {
        const label = `${c.label}${c.futureOperation ? ' ¹' : ''}${c.note ? ' ²' : ''}`;
        return `| ${label} | ${ROLE_DEFINITIONS.map((r) => accessLevel(r.key, c)).join(' | ')} |`;
    });
    return [header, divider, ...rows].join('\n');
}
/** Markdown table of every permission per role. */
export function renderPermissionTableMarkdown() {
    const header = `| Permission | Description | ${ROLE_DEFINITIONS.map((r) => r.name).join(' | ')} |`;
    const divider = `| --- | --- | ${ROLE_DEFINITIONS.map(() => ':-:').join(' | ')} |`;
    const rows = PERMISSION_KEYS.map((p) => `| \`${p}\` | ${PERMISSIONS[p]} | ${ROLE_DEFINITIONS.map((r) => (r.permissions.includes(p) ? '✓' : '–')).join(' | ')} |`);
    return [header, divider, ...rows].join('\n');
}
//# sourceMappingURL=permissions.js.map